import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentMethods = ({ savedMethods, onAddMethod, onSetDefault, onRemoveMethod }) => {
  const [showAddMethod, setShowAddMethod] = useState(false);

  const paymentMethodTypes = [
    {
      type: 'orange_money',
      name: 'Orange Money',
      icon: 'Smartphone',
      description: 'Mobile wallet payment',
      fees: '1.5%',
      color: 'text-orange-600'
    },
    {
      type: 'mtn_momo',
      name: 'MTN Mobile Money',
      icon: 'Smartphone',
      description: 'Mobile wallet payment',
      fees: '1.5%',
      color: 'text-yellow-600'
    },
    {
      type: 'card',
      name: 'Credit/Debit Card',
      icon: 'CreditCard',
      description: 'Visa, Mastercard',
      fees: '2.5%',
      color: 'text-blue-600'
    },
    {
      type: 'wallet',
      name: 'AutoCasse Wallet',
      icon: 'Wallet',
      description: 'Internal wallet balance',
      fees: 'Free',
      color: 'text-primary'
    }
  ];

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const getMethodIcon = (type) => {
    const method = paymentMethodTypes?.find(m => m?.type === type);
    return method?.icon || 'CreditCard';
  };

  const getMethodName = (type) => {
    const method = paymentMethodTypes?.find(m => m?.type === type);
    return method?.name || 'Unknown Method';
  };

  const maskCardNumber = (number) => {
    return `**** **** **** ${number?.slice(-4)}`;
  };

  const maskPhoneNumber = (phone) => {
    return `***-***-${phone?.slice(-4)}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Payment Methods</h2>
          <p className="text-muted-foreground">Manage your payment methods for subscriptions and premium features</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowAddMethod(true)}
          iconName="Plus"
          iconPosition="left"
          iconSize={16}
        >
          Add Method
        </Button>
      </div>
      {/* Saved Payment Methods */}
      <div className="space-y-4 mb-6">
        {savedMethods?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CreditCard" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium text-foreground mb-2">No Payment Methods</h3>
            <p className="text-muted-foreground mb-4">Add a payment method to manage your subscriptions</p>
            <Button
              variant="default"
              onClick={() => setShowAddMethod(true)}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}
            >
              Add Your First Method
            </Button>
          </div>
        ) : (
          savedMethods?.map((method) => (
            <div
              key={method?.id}
              className={`border rounded-lg p-4 ${
                method?.isDefault ? 'border-primary bg-primary/5' : 'border-border'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    method?.isDefault ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon name={getMethodIcon(method?.type)} size={24} />
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-foreground">{getMethodName(method?.type)}</h3>
                      {method?.isDefault && (
                        <span className="bg-primary text-primary-foreground text-xs font-medium px-2 py-1 rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {method?.type === 'card' 
                        ? `${method?.brand} ${maskCardNumber(method?.number)}`
                        : method?.type?.includes('money') || method?.type?.includes('momo')
                        ? maskPhoneNumber(method?.phoneNumber)
                        : `Balance: ${formatAmount(method?.balance || 0)}`
                      }
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Added {new Date(method.addedDate)?.toLocaleDateString('fr-CM')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {!method?.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSetDefault(method?.id)}
                    >
                      Set Default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveMethod(method?.id)}
                    iconName="Trash2"
                    iconSize={16}
                    className="text-error hover:text-error"
                  />
                </div>
              </div>

              {/* Method Status */}
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center space-x-4 text-sm">
                  <div className="flex items-center space-x-1">
                    <Icon 
                      name={method?.isVerified ? "CheckCircle" : "AlertCircle"} 
                      size={14} 
                      className={method?.isVerified ? "text-success" : "text-warning"} 
                    />
                    <span className={method?.isVerified ? "text-success" : "text-warning"}>
                      {method?.isVerified ? 'Verified' : 'Pending Verification'}
                    </span>
                  </div>
                  
                  {method?.lastUsed && (
                    <span className="text-muted-foreground">
                      Last used: {new Date(method.lastUsed)?.toLocaleDateString('fr-CM')}
                    </span>
                  )}
                </div>

                {method?.type === 'wallet' && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    iconSize={14}
                  >
                    Add Funds
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      {/* Available Payment Methods */}
      <div className="border-t border-border pt-6">
        <h3 className="font-medium text-foreground mb-4">Available Payment Methods</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {paymentMethodTypes?.map((method) => (
            <div
              key={method?.type}
              className="border border-border rounded-lg p-4 hover:border-primary/50 transition-smooth"
            >
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-muted ${method?.color}`}>
                  <Icon name={method?.icon} size={20} />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{method?.name}</h4>
                  <p className="text-sm text-muted-foreground">{method?.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">{method?.fees}</p>
                  <p className="text-xs text-muted-foreground">Processing fee</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Security Notice */}
      <div className="mt-6 p-4 bg-muted/25 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-primary mt-0.5" />
          <div>
            <h3 className="font-medium text-foreground mb-1">Secure Payments</h3>
            <p className="text-sm text-muted-foreground">
              All payment information is encrypted and securely stored. We never store your full card details or mobile money PINs.
            </p>
            <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Lock" size={12} />
                <span>256-bit SSL</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Shield" size={12} />
                <span>PCI Compliant</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="CheckCircle" size={12} />
                <span>Bank Grade Security</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add Method Modal Trigger */}
      {showAddMethod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-md bg-card rounded-xl shadow-floating p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Add Payment Method</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddMethod(false)}
                iconName="X"
                iconSize={20}
              />
            </div>
            
            <div className="space-y-3">
              {paymentMethodTypes?.map((method) => (
                <button
                  key={method?.type}
                  onClick={() => {
                    onAddMethod(method?.type);
                    setShowAddMethod(false);
                  }}
                  className="w-full flex items-center justify-between p-3 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-smooth"
                >
                  <div className="flex items-center space-x-3">
                    <Icon name={method?.icon} size={20} className={method?.color} />
                    <div className="text-left">
                      <div className="font-medium text-foreground">{method?.name}</div>
                      <div className="text-sm text-muted-foreground">{method?.description}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-foreground">{method?.fees}</div>
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethods;